import React, { useContext, useState } from 'react'
import globalStyles from "./../../assets/styles/global.module.scss";
import popupStyles from "./../../assets/styles/componentStyles/globalPopupStyles.module.scss";
import contactStyles from "../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss"
import integrationStyles from "../../assets/styles/componentStyles/chatbotStyles/integrations.module.scss";
import phoneStyles from "../../assets/styles/componentStyles/phoneComponent.module.scss"
import { Autocomplete, Divider, Grid, TextField, Typography } from '@mui/material';
import Required from './required';
import GlobalButton from './globalButton/button';
import { useIntegrationStore } from '../../store/integrationStore';
import { useShallow } from 'zustand/react/shallow';
import { wabaVerificationRequestPayload } from '../../models/axiosModel';
import { UserContext, UserContextPayload } from '../../context/userContext';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import parsePhoneNumberFromString, { CountryCode, isValidNumberForRegion } from 'libphonenumber-js';
import validator from 'validator';
import agent from '../../services/api';
import { useGenericStore } from '../../store/genericStore';
import { wabaVerificationDocuments } from '../../utils/constants';

type WabaVerificationFieldKey = keyof Omit<wabaVerificationRequestPayload, 'organization_id'>;

const WabaVerificationRequestPopup = () => {

    const { userDetail, setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { wabaVerificationData, setFlagOpenWabaVerificationForm } = useIntegrationStore(useShallow((state) => ({
        wabaVerificationData: state.wabaVerificationData,
        setFlagOpenWabaVerificationForm: state.setFlagOpenWabaVerificationForm
    })))

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const wabaIntegrationStatus = wabaVerificationData?.organization_waba_integration_status?.toLowerCase();
    const rejectionReason = wabaVerificationData?.latest_request?.rejection_reason?.trim();
    const innerHeight = window.innerHeight - 300;

    const [payload, setPayload] = useState<wabaVerificationRequestPayload>({
        organization_id: Number(userDetail?.user_organization_data?.organization_id ?? 0),
        contact_email: '',
        phone_number: '',
        business_name: '',
        business_address: '',
        available_verification_documents: '',
        website_url: '',
    });

    const [flagValidPhoneNumber, setFlagValidPhoneNumber] = useState<boolean>(true);
    const [flagShowMoreInfo, setFlagShowMoreInfo] = useState<boolean>(false);

    const getFieldValue = (key: WabaVerificationFieldKey): string => {
        return payload[key] ?? '';
    }

    const onChangeOfField = (key: WabaVerificationFieldKey, value: string, data?:  {} | CountryData) => {
        let updatedValue = value;
        if(key === 'phone_number'){
            updatedValue = '+' + value;
            const phoneNumber = parsePhoneNumberFromString('+' + value);

            if (phoneNumber && data && 'countryCode' in data) {
                const valid = isValidNumberForRegion(phoneNumber.number, (data?.countryCode?.toUpperCase()) as CountryCode);
                setFlagValidPhoneNumber(valid);
            } else {
                setFlagValidPhoneNumber(false);
            }
        }
        setPayload((prev) => ({
            ...prev,
            [key]: updatedValue,
        }));
    }

    const getSelectedDocuments = (): string[] => {
        const documents = getFieldValue('available_verification_documents');
        if (!documents.trim()) return [];
        return documents.split(',').map((doc) => doc.trim()).filter((doc) => doc.length > 0);
    }

    const isValidEmail = (email: string) => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail.length) return true;
        return validator.isEmail(trimmedEmail);
    }

    const isValidWebsiteUrl = (url: string) => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl.length) return true;
        return validator.isURL(trimmedUrl, { require_protocol: true });
    }

    const renderFormField = (label: React.ReactNode, placeholder: string, fieldKey: WabaVerificationFieldKey, validationMessage?: string) => (
        <Grid container direction="column" gap={"12px"}>
            <Grid item>
                <Typography className={popupStyles.popupFieldTypography}>
                    {label}
                </Typography>
            </Grid>
            <Grid item>
                <TextField
                    value={getFieldValue(fieldKey)}
                    onChange={(e) => onChangeOfField(fieldKey, e.target.value)}
                    placeholder={placeholder}
                    className={`${contactStyles.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                />
                {validationMessage &&
                    <span className={globalStyles.validationText}>{validationMessage}</span>
                }
            </Grid>
        </Grid>
    );

    const checkDisableSubmitBtn = () => {
        if (
            !payload.contact_email?.trim()?.length ||
            !payload.phone_number?.trim()?.length ||
            !payload.business_name?.trim()?.length ||
            !payload.available_verification_documents?.trim()?.length ||
            !flagValidPhoneNumber ||
            !isValidEmail(payload.contact_email) ||
            !isValidWebsiteUrl(payload.website_url ?? '')
        ) {
            return true;
        }
        return false;
    }

    const onClickOfSubmit = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await agent.submitWabaIntegrationRequest.post(payload);
        if(res?.status){
            showSuccess('WABA verification request submitted successfully.');
            setFlagOpenWabaVerificationForm(false);
        } else {
            handleError(res);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Grid container direction="column" justifyContent="center">
                    <Grid item textAlign="center">
                        <Typography className={popupStyles.popupTitleTypography}>
                            Chronox AI WABA Verification
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider className={popupStyles.popupTitleDivider} />
                    </Grid>

                    {wabaIntegrationStatus === "pending" ?
                        <>
                            <Grid item>
                                <Grid container className={popupStyles.popupBodyGridContainer}>
                                    <Grid item xs={12} className={integrationStyles.wabaApprovalPendingRequestGrid}>
                                        <Typography className={integrationStyles.wabaApprovalPendingStatus}>
                                            Your WABA verification request has been submitted and is currently under review.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item>
                                <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} sx={{ padding: "20px 0" }}>
                                    <Grid item>
                                        <GlobalButton
                                            buttonText="Close"
                                            sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                            className='borderedButtonStyle'
                                            onClick={() => setFlagOpenWabaVerificationForm(false)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                        :
                        <>
                            <Grid item xs={12} sx={{ maxHeight: `${innerHeight}px !important` }} className={`${globalStyles.overflowAuto}`}>
                                {wabaIntegrationStatus === "rejected" &&
                                    <Grid item>
                                        <Grid container className={popupStyles.popupBodyGridContainer} sx={{ paddingBottom: '0 !important' }}>
                                            <Grid item xs={12} className={integrationStyles.wabaApprovalRejectedRequestGrid}>
                                                <Typography className={integrationStyles.wabaApprovalRejectedStatus}>
                                                    Your WABA verification request has been rejected. You can submit a new verification request.
                                                </Typography>
                                                {rejectionReason &&
                                                    <Grid container direction="column" className={integrationStyles.wabaApprovalRejectedReasonContainer}>
                                                        <Typography className={integrationStyles.wabaApprovalRejectedReasonLabel}>
                                                            Rejection Reason
                                                        </Typography>
                                                        <Typography className={integrationStyles.wabaApprovalRejectedReasonText}>
                                                            {rejectionReason}
                                                        </Typography>
                                                    </Grid>
                                                }
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                }

                                <Grid item>
                                    <Grid
                                        container
                                        className={`${popupStyles.popupBodyGridContainer}`}
                                        direction="column"
                                        gap={3}
                                        sx={{
                                            ...(wabaIntegrationStatus === "rejected" ? { paddingTop: '12px !important' } : {}),
                                        }}
                                    >
                                        <Grid item xs={12}>
                                            <Grid container direction="column" className={integrationStyles.wabaVerificationInfoContainer}>
                                                <Typography className={integrationStyles.wabaVerificationInfoTitle}>
                                                    WhatsApp Business (WABA) Integration Request
                                                </Typography>
                                                <Typography className={integrationStyles.wabaVerificationInfoDescription}>
                                                    To set up your WhatsApp Business Account, please provide the required business information and upload one supporting document.
                                                </Typography>

                                                <Grid container direction="column" className={integrationStyles.wabaVerificationInfoSection}>
                                                    <Typography className={integrationStyles.wabaVerificationInfoSectionTitle}>
                                                        Business Information
                                                    </Typography>
                                                    <Typography className={integrationStyles.wabaVerificationInfoSectionSubtitle}>
                                                        Please provide:
                                                    </Typography>
                                                    <ul className={integrationStyles.wabaVerificationInfoList}>
                                                        <li>Business Name</li>
                                                        <li>Business Address</li>
                                                        <li>Business Phone Number</li>
                                                        <li>Business Email Address</li>
                                                        <li>Website URL</li>
                                                    </ul>
                                                    {flagShowMoreInfo &&
                                                        <Typography className={integrationStyles.wabaVerificationInfoNote}>
                                                            <span className={integrationStyles.wabaVerificationInfoNoteLabel}>Note:</span> The Business Name and Address should match your official business records and be visible on your website.
                                                        </Typography>
                                                    }
                                                </Grid>

                                                {flagShowMoreInfo ?
                                                    <>
                                                        <Grid container direction="column" className={integrationStyles.wabaVerificationInfoSection}>
                                                            <Typography className={integrationStyles.wabaVerificationInfoSectionTitle}>
                                                                Supporting Document
                                                            </Typography>
                                                            <Typography className={integrationStyles.wabaVerificationInfoSectionSubtitle}>
                                                                Upload <strong>one</strong> of the following:
                                                            </Typography>
                                                            <ul className={integrationStyles.wabaVerificationInfoList}>
                                                                {wabaVerificationDocuments?.map((document) => {
                                                                    return (
                                                                        <li>{document}</li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </Grid>

                                                        <Grid container direction="column" className={integrationStyles.wabaVerificationInfoSection}>
                                                            <Typography className={integrationStyles.wabaVerificationInfoSectionTitle}>
                                                                Phone Verification
                                                            </Typography>
                                                            <Typography className={integrationStyles.wabaVerificationInfoDescription}>
                                                                Your business phone number should be active and available for verification during the setup process.
                                                            </Typography>
                                                        </Grid>

                                                        <Typography className={integrationStyles.wabaVerificationInfoFooter}>
                                                            Please ensure all information is accurate to avoid delays in the verification process.
                                                        </Typography>
                                                    </>
                                                    :
                                                    <Typography
                                                        className={integrationStyles.wabaVerificationKnowMore}
                                                        onClick={() => setFlagShowMoreInfo(true)}
                                                    >
                                                        Know More
                                                    </Typography>
                                                }
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    {renderFormField(
                                                        <>Contact Email <Required /></>,
                                                        'Enter Contact Email',
                                                        'contact_email',
                                                        !isValidEmail(payload.contact_email) ? 'Please enter valid Email.' : undefined,
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Grid container direction="column" gap={"12px"}>
                                                        <Grid item>
                                                            <Typography className={popupStyles.popupFieldTypography}>
                                                                Phone Number <Required />
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item className={`${globalStyles.fullwidth} ${phoneStyles.phoneInputWrapperForRequestACallback}`}>
                                                            <PhoneInput 
                                                                country="in"
                                                                value={payload.phone_number}
                                                                onChange={(value, data) => onChangeOfField('phone_number', value, data)}
                                                            /> 
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    {renderFormField(<>Business Name <Required /></>, 'Enter Business Name', 'business_name')}
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    {renderFormField('Business Address', 'Enter Business Address', 'business_address')}
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Grid container direction="column" gap={"12px"}>
                                                        <Grid item>
                                                            <Typography className={popupStyles.popupFieldTypography}>
                                                                Available Verfication Documents <Required />
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                            <Autocomplete
                                                                className={`${contactStyles.contactFilterDropdownsInPopup} ${integrationStyles.verificationDocumentDropdown}`}
                                                                options={wabaVerificationDocuments}
                                                                getOptionLabel={(option: string) => option}
                                                                multiple
                                                                value={getSelectedDocuments()}
                                                                onChange={(_: React.SyntheticEvent, value: string[]) => onChangeOfField('available_verification_documents', value.join(', '))}
                                                                ListboxProps={{className: integrationStyles.autocompleteListbox,}}
                                                                renderInput={(params) => {
                                                                    const { InputProps, ...restParams } = params;
                                                                    const { startAdornment, ...restInputProps } = InputProps;
                                                                    return (
                                                                        <TextField
                                                                            {...restParams}
                                                                            placeholder="Select Available Verfication Documents"
                                                                            className={`${contactStyles.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                                                                            sx={{
                                                                                '& .MuiInputBase-input::placeholder': {
                                                                                    fontWeight: '500 !important',
                                                                                },
                                                                            }}
                                                                            InputProps={{
                                                                                ...restInputProps,
                                                                                startAdornment: (
                                                                                    <div style={{
                                                                                        maxHeight: "79px",
                                                                                        overflowY: 'auto',
                                                                                    }}
                                                                                    >
                                                                                        {startAdornment}
                                                                                    </div>
                                                                                ),
                                                                            }}
                                                                        />
                                                                    )
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    {renderFormField(
                                                        'Website URL',
                                                        'Enter Website URL',
                                                        'website_url',
                                                        !isValidWebsiteUrl(payload.website_url ?? '') ? 'Please enter valid URL.' : undefined,
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item>
                                <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} sx={{ padding: "20px 0" }}>
                                    <Grid item>
                                        <GlobalButton
                                            buttonText="Cancel"
                                            sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                            className='borderedButtonStyle'
                                            onClick={() => setFlagOpenWabaVerificationForm(false)}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <GlobalButton
                                            buttonText="Submit"
                                            sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                            className='primaryButtonStyle'
                                            disabled={checkDisableSubmitBtn()}
                                            onClick={() => onClickOfSubmit()}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    }

                </Grid>
            </Grid>
        </Grid>
    )
}

export default WabaVerificationRequestPopup
